import { useState } from 'react';

interface UnlockProps {
  onUnlocked: () => void;
}

const STANDALONE =
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true);

export function Unlock({ onUnlocked }: UnlockProps) {
  const [passcode, setPasscode] = useState('');
  const [state, setState] = useState<'idle' | 'checking' | 'wrong' | 'waiting' | 'error'>('idle');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!passcode.trim()) return;
    setState('checking');
    try {
      const res = await fetch('/api/unlock', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ passcode }),
      });
      if (res.ok) {
        onUnlocked();
        return;
      }
      setState(res.status === 429 ? 'waiting' : res.status === 401 ? 'wrong' : 'error');
    } catch {
      setState('error');
    }
  }

  const message =
    state === 'wrong'
      ? 'That passcode did not match. Check the message it came with and try again.'
      : state === 'waiting'
        ? 'Too many tries. Wait a minute, then try once more.'
        : state === 'error'
          ? 'Something went wrong. Check your connection and try again.'
          : '';

  return (
    <div className="unlock">
      <div className="unlock__panel">
        <h1 className="unlock__title">Seemantham</h1>
        <p className="unlock__sub">Photos and clips from 26 August 2026</p>

        <form className="unlock__form" onSubmit={submit}>
          <label className="unlock__label" htmlFor="passcode">
            Passcode
          </label>
          <input
            id="passcode"
            className="unlock__input"
            value={passcode}
            onChange={(event) => setPasscode(event.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            enterKeyHint="go"
            autoFocus
            aria-describedby="unlock-message"
          />
          <button type="submit" className="button--primary" disabled={state === 'checking'}>
            {state === 'checking' ? 'Opening' : 'Open the album'}
          </button>
        </form>

        <p className="unlock__message" id="unlock-message" role="status" aria-live="polite">
          {message}
        </p>

        {STANDALONE ? (
          <p className="unlock__note">
            The app keeps its own login, so the passcode is needed once more here.
          </p>
        ) : null}
      </div>
    </div>
  );
}
